import { Inject, Injectable } from '@angular/core';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  OfferDetail,
  OfferDetailModalComponent,
  ProcessedTvmOffer,
  SelectedOffer,
  TargetedOffersFacadeService,
} from '@rogers/self-serve-targeted-offers';
import { ModalInjectableData } from '@rogers/cdk/common/classes';
import { ModalService } from '@rogers/cdk/modal';
import { RedeemOffersService } from '../redeem-offers/redeem-offers.service';
import { ViewContainerRefService } from '@rci/self-serve-fido/shared/view-container-ref/view-container-ref.service';
import { modalWindowConfig } from '@rci/self-serve-fido/shared/modal-window.config';
import { TranslateService } from '@ngx-translate/core';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { AnalyticsServiceProxy } from '@rci/self-serve-fido/core/analytics';
import { ICompleteUniversalDataLayer } from '@rogers/analytics';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { AccountListFacadeService, AccountSubscription } from '@rogers/self-serve-common/account';
import { SESSION_STORAGE } from '@rci/storage/session-storage.service';
import { UserAccount } from '@rogers/self-serve-common';

const pageName = 'modal-S&D offer';
const internalTrackingEventObject = {
  events: {
    interaction: true,
  },
  isInternalLinkTracking: true,
};

@Injectable()
export class TargetedOffersService {
  private offersList: OfferDetail[] = [];

  constructor(
    private readonly accountDetailService: AccountDetailService,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly analyticsService: AnalyticsServiceProxy,
    private readonly modalService: ModalService,
    private readonly redeemOffersService: RedeemOffersService,
    private readonly targetedOffersFacadeService: TargetedOffersFacadeService,
    private readonly translateService: TranslateService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
    private readonly viewContainerRefService: ViewContainerRefService,
    @Inject(SESSION_STORAGE) private readonly sessionStorage: Storage,
  ) {}

  showTargetedOffersModal() {
    const accountList$ = this.accountListFacadeService.accountList$.pipe(
      map(accountsList =>
        accountsList.filter(
          account => account.isWirelessOrConsolidatedAccount && account.isActiveAccount && !account.isSubscriber,
        ),
      ),
      take(1),
    );

    return accountList$.pipe(
      switchMap(accounts =>
        this.getTargetedOffers$(accounts).pipe(
          filter(targetedOffers => !!targetedOffers && accounts.length === targetedOffers.length),
          take(1),
          map(offersList => {
            return offersList.flatMap(offers =>
              this.getOffersList(offers.accountNumber, offers.subscriptions, offers.targetedOffers),
            );
          }),
          switchMap(targetedOffers => {
            this.offersList = targetedOffers;
            if (targetedOffers.length === 0 || this.sessionStorage.getItem('hideTargetedOfferModal') === 'true') {
              return EMPTY;
            }
            return this.showOfferModal(this.translateService.currentLang);
          }),
        ),
      ),
    );
  }

  showOfferModal(language: string, ctn?: string) {
    this.sessionStorage.setItem('hideTargetedOfferModal', 'true');

    const selectedCtn = ctn ?? this.offersList[0].ctn;

    const modalData = new ModalInjectableData({
      language,
      selectedCtn,
      offersList: this.offersList,
    });

    const modalRef = this.modalService.open(
      OfferDetailModalComponent,
      this.viewContainerRefService.viewContainerRef,
      modalWindowConfig,
      modalData,
    );

    this.trackTargetedOffers(this.offersList);

    modalRef
      .afterClosed()
      .pipe(
        take(1),
        tap(reasonToClose => {
          if (reasonToClose !== 'nodecline') {
            const payload: ICompleteUniversalDataLayer = {
              ...this.getClickInteractionTrackingObject('modal-S&D offer-close'),
              events: {
                interaction: true,
              },
            };
            this.analyticsService.track(payload, true, null, null);
          }
        }),
      )
      .subscribe();

    return modalRef.componentInstance.offerClick.pipe(
      switchMap(({ selectedOffer, targetedOffer }: { selectedOffer: SelectedOffer; targetedOffer: OfferDetail }) => {
        modalRef.close('nodecline');
        this.trackOfferClickEvent(selectedOffer, targetedOffer);
        return this.handleSelectedTargetedOffers(targetedOffer, selectedOffer);
      }),
    );
  }

  private handleSelectedTargetedOffers(targetedOffer: OfferDetail, selectedOffer: SelectedOffer) {
    if (selectedOffer.noTransactionBpoSelected) {
      return this.redeemOffersService.redeemOffer$(targetedOffer).pipe(
        tap(result => {
          if (result) {
            this.updateOffersList(targetedOffer);
          }
        }),
      );
    }

    this.userInformationStateFacade.updateSelectedSubscriberNo(targetedOffer.ctn);
    this.userInformationStateFacade.updateSelectedAccountNumber(targetedOffer.accountNumber);
    return EMPTY;
  }

  private updateOffersList(targetedOffer: OfferDetail) {
    this.offersList = this.offersList.filter(offer => offer.ctn !== targetedOffer.ctn);
  }

  private getOffersList(
    accountNumber: string,
    subscriptions: AccountSubscription[],
    offers: OfferDetail[],
  ): OfferDetail[] {
    return offers.map(offer => {
      let subscriberFirstName = '';
      let subscriberName = '';
      const subscription = subscriptions.find(sub => sub.subNumber === offer.ctn);
      if (subscription) {
        subscriberFirstName = subscription.firstName || '';
        subscriberName = [subscription.firstName, subscription.lastName].filter(Boolean).join(' ');
      }
      return { ...offer, accountNumber, subscriberFirstName, subscriberName };
    });
  }

  private getTargetedOffers$(accounts: UserAccount[]) {
    const accountDetails$ = accounts
      .filter(account => account.isWirelessOrConsolidatedAccount && account.isActiveAccount && !account.isSubscriber)
      .map(account => this.accountDetailService.getAccountDetail$(account.accountNumber));

    return combineLatest(accountDetails$).pipe(
      switchMap(accountDetails => {
        const targetedOffers$ = accountDetails.map(accountDetail => {
          if (accountDetail.hasError || !accountDetail.subscriptions || accountDetail.subscriptions.length === 0) {
            return of({
              accountNumber: accountDetail.accountNumber,
              subscriptions: [],
              targetedOffers: [],
            });
          }

          return this.targetedOffersFacadeService
            .getTargetedOffers$(accountDetail.accountNumber, accountDetail.billingAddress?.province ?? 'ON')
            .pipe(
              filter(targetedOffers => !!targetedOffers),
              map(targetedOffers => {
                return {
                  accountNumber: accountDetail.accountNumber,
                  subscriptions: accountDetail.subscriptions,
                  targetedOffers,
                };
              }),
            );
        });
        return combineLatest(targetedOffers$);
      }),
    );
  }

  private trackOfferClickEvent(selectedOffer: SelectedOffer, targetedOffer: OfferDetail) {
    let payload: ICompleteUniversalDataLayer;
    if (selectedOffer.noTransactionBpoSelected) {
      payload = {
        ...this.getClickInteractionTrackingObject('bpo-get this offer'),
        events: {
          buyFlowStart: true,
          interaction: true,
        },
        buyflow: {
          useCase: `${targetedOffer.noTransactionalBpo.bpoId}|bpo`,
          name: 'bpo',
        },
      };
    } else if (selectedOffer.tvmSelected) {
      payload = {
        ...this.getClickInteractionTrackingObject('tvm-get this offer'),
        ...internalTrackingEventObject,
      };
    } else if (selectedOffer.seeMorePlanOffers) {
      payload = {
        ...this.getClickInteractionTrackingObject('S&D offer-upgrade my plan'),
        ...internalTrackingEventObject,
      };
    }
    this.analyticsService.track(payload, true, null, null);
  }

  private trackTargetedOffers(offers: OfferDetail[]) {
    const payload: ICompleteUniversalDataLayer = {
      page: {
        name: pageName,
        hierarchy: ['myaccount', 'overview'],
      },
      events: {
        pageView: true,
      },
      ecommerce: {
        offers: this.processTargetedOffers(offers),
      },
    };
    this.analyticsService.track(payload, true, null, null);
  }

  private getClickInteractionTrackingObject(name: string) {
    return {
      page: {
        name: pageName,
        hierarchy: [],
      },
      interaction: {
        type: 'click',
        name,
      },
    };
  }

  private processTargetedOffers(offers: OfferDetail[]) {
    return offers.flatMap(offer => {
      return [
        offer.tvmPresent ? this.populateOfferCodeType(offer.tvmOffer) : null,
        offer.noTransactionalBpoPresent ? this.populateBpoCodeType(offer.noTransactionalBpo) : null,
      ].filter(Boolean);
    });
  }

  private populateBpoCodeType(bpo) {
    return {
      code: bpo.bpoId,
      type: bpo.bpoTypeForAnalytics,
    };
  }

  private populateOfferCodeType(offer: ProcessedTvmOffer) {
    return {
      code: offer.externalId,
      type: offer.offerType,
    };
  }
}
